import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Checkbox, Alert,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { addonsAPI, addonOptionsAPI } from '../../services/api';
import ConfirmDialog from '../../components/ConfirmDialog';
import { Addon, AddonOption } from '../../types';

interface AddonOptionFormData {
  AddonId: number | string;
  OptionName: string;
  Description: string;
  DisplayOrder: number;
  IsActive: boolean;
}

const AddonOptions = () => {
  const [addons, setAddons] = useState<Addon[]>([]);
  const [addonOptions, setAddonOptions] = useState<AddonOption[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<AddonOption[]>([]);
  const [selectedAddon, setSelectedAddon] = useState<number | string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOption, setEditingOption] = useState<AddonOption | null>(null);
  const [formData, setFormData] = useState<AddonOptionFormData>({ AddonId: '', OptionName: '', Description: '', DisplayOrder: 1, IsActive: true });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; id: number }>({ open: false, id: 0 });

  useEffect(() => { fetchAddons(); fetchAddonOptions(); }, []);

  useEffect(() => {
    if (selectedAddon) {
      const filtered = addonOptions.filter((opt) => opt.AddonId === Number(selectedAddon));
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(addonOptions);
    }
  }, [selectedAddon, addonOptions]);

  const fetchAddons = async () => {
    try { const response = await addonsAPI.getAll(); setAddons(response.data.filter((a: Addon) => a.IsActive)); }
    catch (err) { setError('Failed to load addons'); }
  };

  const fetchAddonOptions = async () => {
    try { const response = await addonOptionsAPI.getAll(); setAddonOptions(response.data); }
    catch (err) { setError('Failed to load addon options'); }
  };

  const handleOpenDialog = (option: AddonOption | null = null) => {
    if (option) {
      setEditingOption(option);
      setFormData({ AddonId: option.AddonId, OptionName: option.OptionName, Description: option.Description || '', DisplayOrder: option.DisplayOrder, IsActive: !!option.IsActive });
    } else {
      setEditingOption(null);
      setFormData({ AddonId: selectedAddon || '', OptionName: '', Description: '', DisplayOrder: 1, IsActive: true });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => { setDialogOpen(false); setEditingOption(null); setFormData({ AddonId: '', OptionName: '', Description: '', DisplayOrder: 1, IsActive: true }); };

  const handleSubmit = async () => {
    try {
      if (editingOption) {
        await addonOptionsAPI.update(editingOption.AddonOptionId, { optionName: formData.OptionName, description: formData.Description, displayOrder: formData.DisplayOrder, isActive: formData.IsActive });
        setSuccess('Addon option updated successfully');
      } else {
        await addonOptionsAPI.create({ addonId: formData.AddonId, optionName: formData.OptionName, description: formData.Description, displayOrder: formData.DisplayOrder });
        setSuccess('Addon option created successfully');
      }
      handleCloseDialog(); fetchAddonOptions(); setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError('Failed to save addon option'); }
  };

  const handleDeleteClick = (id: number) => {
    setConfirmDialog({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    const id = confirmDialog.id;
    setConfirmDialog({ open: false, id: 0 });
    try { await addonOptionsAPI.delete(id); setSuccess('Addon option deleted successfully'); fetchAddonOptions(); setTimeout(() => setSuccess(''), 3000); }
    catch (err) { setError('Failed to delete addon option'); }
  };

  const getAddonName = (addonId: number) => {
    const addon = addons.find((a) => a.AddonId === addonId);
    return addon ? addon.AddonName : 'Unknown';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Addon Options</Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>Add Addon Option</Button>
      </Box>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField select label="Filter by Addon" value={selectedAddon} onChange={(e) => setSelectedAddon(e.target.value)} fullWidth size="small">
          <MenuItem value="">All Addons</MenuItem>
          {addons.map((addon) => (<MenuItem key={addon.AddonId} value={addon.AddonId}>{addon.AddonName}</MenuItem>))}
        </TextField>
      </Paper>
      <TableContainer component={Paper}>
        <Table>
          <TableHead><TableRow>
            <TableCell>Addon</TableCell><TableCell>Option Name</TableCell><TableCell>Description</TableCell><TableCell>Display Order</TableCell><TableCell>Active</TableCell><TableCell align="right">Actions</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {filteredOptions.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center">No addon options found</TableCell></TableRow>
            ) : (
              filteredOptions.map((option) => (
                <TableRow key={option.AddonOptionId}>
                  <TableCell>{getAddonName(option.AddonId)}</TableCell><TableCell>{option.OptionName}</TableCell>
                  <TableCell>{option.Description || '-'}</TableCell><TableCell>{option.DisplayOrder}</TableCell>
                  <TableCell><Checkbox checked={!!option.IsActive} disabled /></TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenDialog(option)} color="primary"><EditIcon /></IconButton>
                    <IconButton size="small" onClick={() => handleDeleteClick(option.AddonOptionId)} color="error"><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingOption ? 'Edit Addon Option' : 'Add Addon Option'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField select label="Addon" value={formData.AddonId} onChange={(e) => setFormData({ ...formData, AddonId: e.target.value })} required fullWidth>
              {addons.map((addon) => (<MenuItem key={addon.AddonId} value={addon.AddonId}>{addon.AddonName}</MenuItem>))}
            </TextField>
            <TextField label="Option Name" value={formData.OptionName} onChange={(e) => setFormData({ ...formData, OptionName: e.target.value })} required fullWidth />
            <TextField label="Description" value={formData.Description} onChange={(e) => setFormData({ ...formData, Description: e.target.value })} multiline rows={3} fullWidth />
            <TextField label="Display Order" type="number" value={formData.DisplayOrder} onChange={(e) => setFormData({ ...formData, DisplayOrder: parseInt(e.target.value) })} required fullWidth />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox checked={formData.IsActive} onChange={(e) => setFormData({ ...formData, IsActive: e.target.checked })} />
              <Typography>Active</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!formData.AddonId || !formData.OptionName}>{editingOption ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.open}
        title="Delete Addon Option"
        message="Are you sure you want to delete this addon option?"
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDialog({ open: false, id: 0 })}
      />
    </Box>
  );
};

export default AddonOptions;
